import { useEffect, useState } from 'react'
import { ArrowTopRightOnSquareIcon, PhotoIcon } from '@heroicons/react/24/outline'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LogarithmicScale,
  Tooltip,
  type TooltipItem,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { AdManagementService } from '../../client'
import { formatCurrency, formatDate, formatNumber, formatPercent, titleCase } from '../../lib/format'
import Modal from '../ui/Modal'

Chart.register(Tooltip, Legend, BarController, BarElement, CategoryScale, LinearScale, LogarithmicScale)

export type AdRow = {
  ad_id: string
  ad_name?: string | null
  status?: string
  created_time?: string | null
  published_at?: string | null
  thumbnail_url?: string | null
  is_adlovin_created?: boolean
  campaign?: { name?: string; objective?: string; status?: string; buying_type?: string }
  adset?: { name?: string; bid_strategy?: string; targeting?: Record<string, unknown> }
  insights?: Record<string, unknown>
  deleted?: boolean
}

type Detail = AdRow & {
  name?: string
  effective_status?: string
  insights?: Record<string, unknown>
}

const CHART_BARS = 6
const CHART_COLORS = ['#0074e8', '#fcb0b3', '#f9dc5c', '#7d5cbe', '#5cbe68', '#696969']
const FONT = 'Inter, system-ui, sans-serif'

const ACTION_NAMES: Record<string, string> = {
  link_click: 'Link Clicks',
  post_engagement: 'Post Engagement',
  page_engagement: 'Page Engagement',
  video_view: 'Video Views',
  post_reaction: 'Reactions',
  comment: 'Comments',
  like: 'Likes',
  share: 'Shares',
  'onsite_conversion.post_save': 'Post Saves',
  landing_page_view: 'Landing Page Views',
  photo_view: 'Photo Views',
}

const barChartOptions = {
  indexAxis: 'y' as const,
  maintainAspectRatio: false,
  responsive: true,
  layout: { padding: { right: 8 } },
  scales: {
    x: {
      type: 'logarithmic' as const,
      border: { display: false },
      grid: { color: '#f1f5f9' },
      ticks: {
        color: '#94a3b8',
        font: { size: 10, family: FONT },
        maxTicksLimit: 4,
        callback: (value: string | number) => {
          const count = Number(value)
          if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(0)}M`
          if (count >= 1_000) return `${(count / 1_000).toFixed(0)}K`
          return String(count)
        },
      },
    },
    y: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: '#475569', font: { size: 11, family: FONT } },
    },
  },
  elements: { bar: { borderRadius: 4, borderSkipped: false as const } },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b',
      titleFont: { size: 11, family: FONT },
      bodyFont: { size: 12, family: FONT },
      padding: 8,
      cornerRadius: 6,
      callbacks: {
        label: ({ parsed }: TooltipItem<'bar'>) => ` ${Number(parsed.x).toLocaleString()}`,
      },
    },
  },
}

function videoMetric(value: unknown) {
  if (!Array.isArray(value) || value.length === 0) return '—'
  return Number((value[0] as { value?: unknown }).value ?? 0).toLocaleString()
}

function statusBadge(status?: string) {
  const base = 'rounded-full px-2 py-0.5 text-xs font-medium'
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return `${base} bg-green-50 text-green-700`
    case 'PAUSED':
      return `${base} bg-yellow-50 text-yellow-700`
    case 'ARCHIVED':
    case 'DELETED':
      return `${base} bg-gray-100 text-gray-600`
    default:
      return `${base} bg-gray-100 text-gray-600`
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-gray-500">{title}</p>
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">{children}</div>
    </div>
  )
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-1.5 text-sm">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 truncate text-right text-gray-800">{value || '—'}</span>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <p className="text-[10px] text-gray-400 uppercase">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  )
}

type Props = {
  ad: AdRow
  brandId: number
  adAccountId?: string | null
  onClose: () => void
}

export default function AdDetailDialog({ ad, brandId, adAccountId, onClose }: Props) {
  const [detail, setDetail] = useState<Detail>(ad)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    AdManagementService.getAdDetail(String(ad.ad_id), brandId)
      .then((data) => setDetail({ ...ad, ...(data as Detail) }))
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }, [ad, brandId])

  const insights = detail.insights ?? {}
  const actions = Array.isArray(insights.actions) ? (insights.actions as { action_type: string; value: string }[]) : []
  const topActions = actions
    .map(({ action_type, value }) => ({
      label: ACTION_NAMES[action_type] ?? action_type.replace(/_/g, ' '),
      value: Number(value),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, CHART_BARS)
  const isVideo = Object.keys(insights).some((key) => key.startsWith('video_'))
  const accountNumericId = adAccountId?.replace('act_', '')

  return (
    <Modal title="Ad Detail" width="max-w-2xl" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase">Meta Ads</p>

        <div className="flex gap-4">
          <div className="shrink-0">
            {detail.thumbnail_url ? (
              <img src={detail.thumbnail_url} alt="" className="h-36 w-24 rounded-lg object-cover" />
            ) : (
              <div className="flex h-36 w-24 items-center justify-center rounded-lg bg-gray-200">
                <PhotoIcon className="size-7 text-gray-400" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-1.5">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {detail.name || detail.ad_name || `Ad #${detail.ad_id}`}
            </h3>
            <div className="flex items-center gap-2 text-sm">
              <span className={statusBadge(detail.effective_status || detail.status)}>
                {titleCase(detail.effective_status || detail.status)}
              </span>
              {detail.effective_status && detail.effective_status !== detail.status && (
                <span className="text-xs text-gray-400">(delivery: {detail.effective_status})</span>
              )}
            </div>
            {detail.created_time && (
              <p className="text-xs text-gray-400">Created {formatDate(detail.created_time)}</p>
            )}
            {typeof insights.date_start === 'string' && (
              <p className="text-xs text-gray-400">
                Reporting: {formatDate(insights.date_start)} – {formatDate(insights.date_stop as string)}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <span className="size-6 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500" />
            <span className="text-sm text-gray-400">Loading full details from Meta…</span>
          </div>
        ) : (
          <>
            <Section title="Campaign">
              <Row label="Name" value={detail.campaign?.name} />
              <Row label="Status" value={titleCase(detail.campaign?.status)} />
              <Row label="Objective" value={titleCase(detail.campaign?.objective?.replace('OUTCOME_', ''))} />
              <Row label="Buying type" value={titleCase(detail.campaign?.buying_type)} />
            </Section>

            <Section title="Ad Set">
              <Row label="Name" value={detail.adset?.name} />
              <Row label="Bid strategy" value={titleCase(detail.adset?.bid_strategy)} />
            </Section>

            <div>
              <p className="mb-1.5 text-xs font-medium text-gray-500">Performance</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Metric label="Impressions" value={formatNumber(insights.impressions)} />
                <Metric label="Reach" value={formatNumber(insights.reach)} />
                <Metric
                  label="Frequency"
                  value={insights.frequency ? Number(insights.frequency).toFixed(2) : '—'}
                />
                <Metric label="Clicks" value={formatNumber(insights.clicks)} />
                <Metric label="Unique clicks" value={formatNumber(insights.unique_clicks)} />
                <Metric label="Link clicks" value={formatNumber(insights.inline_link_clicks)} />
                <Metric label="CTR" value={formatPercent(insights.ctr)} />
                <Metric label="Unique CTR" value={formatPercent(insights.unique_ctr)} />
                <Metric label="Link CTR" value={formatPercent(insights.inline_link_click_ctr)} />
                <Metric label="CPC" value={formatCurrency(insights.cpc)} />
                <Metric label="CPM" value={formatCurrency(insights.cpm)} />
                <Metric label="Spend" value={formatCurrency(insights.spend)} />
              </div>
            </div>

            <Section title="Ad Quality">
              <Row label="Quality" value={titleCase(insights.quality_ranking as string)} />
              <Row label="Engagement rate" value={titleCase(insights.engagement_rate_ranking as string)} />
              <Row label="Conversion rate" value={titleCase(insights.conversion_rate_ranking as string)} />
            </Section>

            {isVideo && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-500">Video</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <Metric label="Avg watch time" value={videoMetric(insights.video_avg_time_watched_actions)} />
                  <Metric label="25% watched" value={videoMetric(insights.video_p25_watched_actions)} />
                  <Metric label="50% watched" value={videoMetric(insights.video_p50_watched_actions)} />
                  <Metric label="75% watched" value={videoMetric(insights.video_p75_watched_actions)} />
                  <Metric label="100% watched" value={videoMetric(insights.video_p100_watched_actions)} />
                </div>
              </div>
            )}

            {topActions.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-500">Actions Breakdown</p>
                <div className="h-48 rounded-lg bg-gray-50 p-3">
                  <Bar
                    options={barChartOptions}
                    data={{
                      labels: topActions.map(({ label }) => label),
                      datasets: [{ data: topActions.map(({ value }) => value), backgroundColor: CHART_COLORS }],
                    }}
                  />
                </div>
              </div>
            )}

            {accountNumericId && (
              <a
                href={`https://adsmanager.facebook.com/adsmanager/manage/ads?act=${accountNumericId}&selected_ad_ids=${detail.ad_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 transition hover:text-blue-700"
              >
                Go to Campaign
                <ArrowTopRightOnSquareIcon className="size-4" />
              </a>
            )}
          </>
      )}
      </div>
    </Modal>
  )
}
